import { type Atom, type PrimitiveAtom, atom } from "jotai";
import { selectAtom, splitAtom } from "jotai/utils";
import { match } from "ts-pattern";
import { z } from "zod";

import { i18n } from "@lingui/core";

import { getEnumOptions, getSchemaFieldType, unwrapZodType } from "./schema.ts";
import type {
  ArrayField,
  BooleanField,
  DateField,
  Field,
  FormErrors,
  FormMessage,
  InferFieldsFromSchema,
  ItemAtom,
  NumberField,
  ObjectField,
  SelectField,
  SubmitResult,
  TextField,
  ZodObjectSchema,
} from "./types.ts";

const NO_MESSAGES: FormMessage[] = [];

function nullToUndefined(value: unknown): unknown {
  if (null === value) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.map(nullToUndefined);
  }

  if ("object" === typeof value && null !== value) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, nullToUndefined(item)]),
    );
  }

  return value;
}

function coerceInitial<TData>(
  schema: ZodObjectSchema,
  data: TData,
  normalizeNull: boolean,
): TData {
  const candidate = normalizeNull ? (nullToUndefined(data) as TData) : data;

  const result = schema.safeParse(candidate);

  if (result.success) {
    return result.data as TData;
  }

  const fallback = { ...(candidate as object) } as Record<string, unknown>;

  for (const [key, fieldSchema] of Object.entries(schema.shape)) {
    if (unwrapZodType(fieldSchema as z.ZodTypeAny) instanceof z.ZodArray) {
      fallback[key] ??= [];
    }
  }

  return fallback as TData;
}

function issuesByPath(schema: ZodObjectSchema, data: unknown): FormErrors {
  const result = schema.safeParse(data);

  if (result.success) {
    return {};
  }

  const errors: FormErrors = {};

  for (const issue of result.error.issues) {
    const path = issue.path.join(".");

    errors[path] = [...(errors[path] ?? []), issue.message];
  }

  return errors;
}

export function normalizeErrorPath(path: string): string {
  return path.replace(/\[(\d+)\]/g, ".$1");
}

function sameMessages(a: FormMessage[], b: FormMessage[]): boolean {
  return (
    a.length === b.length && a.every((message, index) => message === b[index])
  );
}

export function messageText(message: FormMessage): string {
  if ("string" === typeof message) {
    return message;
  }

  return i18n.locale ? i18n._(message) : (message.message ?? message.id);
}

type FormRuntime = {
  issues: Atom<FormErrors>;
  external: PrimitiveAtom<FormErrors>;
  touched: PrimitiveAtom<Record<string, true>>;
  submitted: PrimitiveAtom<boolean>;
  mounted: PrimitiveAtom<Record<string, number>>;
};

function fieldCommon(runtime: FormRuntime, path: string) {
  const rawAtom = atom((get) => ({
    issues: get(runtime.issues)[path] ?? NO_MESSAGES,
    external: get(runtime.external)[path] ?? NO_MESSAGES,
    show: get(runtime.submitted) || true === get(runtime.touched)[path],
  }));

  const errorsAtom = selectAtom(
    rawAtom,
    ({ issues, external, show }) =>
      0 < external.length ? external : show ? issues : NO_MESSAGES,
    sameMessages,
  );

  return {
    path,
    errorsAtom,
    errorAtom: selectAtom(errorsAtom, (messages) =>
      0 === messages.length ? null : messageText(messages[0]!),
    ),
    touchedAtom: selectAtom(
      runtime.touched,
      (touched) => true === touched[path],
    ),
    touch: atom(null, (_get, set) => {
      set(runtime.touched, (previous) =>
        true === previous[path] ? previous : { ...previous, [path]: true },
      );
    }),
    mount: atom(null, (_get, set) => {
      const count = (by: number) =>
        set(runtime.mounted, (previous) => {
          const next = (previous[path] ?? 0) + by;

          if (0 < next) {
            return { ...previous, [path]: next };
          }

          const rest = { ...previous };

          delete rest[path];

          return rest;
        });

      count(1);

      return () => count(-1);
    }),
  };
}

function touchOnWrite<TValue>(
  target: PrimitiveAtom<TValue>,
  runtime: FormRuntime,
  path: string,
): PrimitiveAtom<TValue> {
  return atom(
    (get) => get(target),
    (_get, set, update: TValue) => {
      set(target, update);
      set(runtime.touched, (previous) =>
        true === previous[path] ? previous : { ...previous, [path]: true },
      );
    },
  ) as PrimitiveAtom<TValue>;
}

function createAtomForField<TData, K extends keyof TData>(
  formAtom: PrimitiveAtom<TData>,
  key: K,
): PrimitiveAtom<TData[K] | undefined> {
  return atom(
    (get) => {
      const data = get(formAtom);

      return data[key] as TData[K] | undefined;
    },
    (get, set, update: TData[K]) => {
      const previous = get(formAtom);

      set(formAtom, { ...previous, [key]: update });
    },
  ) as PrimitiveAtom<TData[K] | undefined>;
}

export function atomTree<
  TSchema extends ZodObjectSchema,
  TData = z.infer<TSchema>,
>(
  schema: TSchema,
  baseAtom: PrimitiveAtom<TData>,
  runtime: FormRuntime,
  pathPrefix = "",
) {
  const fields = {} as Record<keyof TData, Field>;

  for (const [key, fieldSchema] of Object.entries(schema.shape) as [
    keyof TData,
    z.ZodTypeAny,
  ][]) {
    const fieldType = getSchemaFieldType(fieldSchema);

    const fieldPath = `${pathPrefix}${String(key)}`;

    const common = { ...fieldCommon(runtime, fieldPath), type: fieldType };

    fields[key] = match(fieldType)
      .returnType<Field>()

      .with("object", () => {
        const nestedSchema = unwrapZodType(fieldSchema) as ZodObjectSchema;

        const nestedAtom = createAtomForField(
          baseAtom,
          key,
        ) as unknown as PrimitiveAtom<z.infer<typeof nestedSchema>>;

        return {
          ...common,
          fields: atomTree(nestedSchema, nestedAtom, runtime, `${fieldPath}.`)
            .fields,
        } as ObjectField<typeof nestedSchema>;
      })

      .with("array", () => {
        const rawFieldAtom = createAtomForField(
          baseAtom,
          key,
        ) as unknown as PrimitiveAtom<unknown[] | null | undefined>;

        const fieldAtom = atom(
          (get) => {
            const value = get(rawFieldAtom);

            return Array.isArray(value) ? value : [];
          },
          (_get, set, update: unknown[]) => {
            set(rawFieldAtom, update);
          },
        ) as unknown as PrimitiveAtom<unknown[]>;

        const itemAtomsAtom = splitAtom(fieldAtom);

        const itemSchema = unwrapZodType(
          (unwrapZodType(fieldSchema) as z.ZodArray<z.ZodTypeAny>).element,
        );

        const itemCache = new WeakMap<
          object,
          { index: number; field: Field }
        >();

        const itemField = (itemAtom: ItemAtom, index: number): Field => {
          const cached = itemCache.get(itemAtom);

          if (cached && cached.index === index) {
            return cached.field;
          }

          const itemPath = `${fieldPath}.${index}`;

          const field =
            itemSchema instanceof z.ZodObject
              ? ({
                  ...fieldCommon(runtime, itemPath),
                  type: "object",
                  fields: atomTree(
                    itemSchema,
                    itemAtom as unknown as PrimitiveAtom<unknown>,
                    runtime,
                    `${itemPath}.`,
                  ).fields,
                } as unknown as Field)
              : ({
                  ...fieldCommon(runtime, itemPath),
                  type: getSchemaFieldType(itemSchema),
                  atom: touchOnWrite(
                    itemAtom as unknown as PrimitiveAtom<unknown>,
                    runtime,
                    itemPath,
                  ),
                } as unknown as Field);

          itemCache.set(itemAtom, { index, field });

          return field;
        };

        const itemsAtom = atom((get) =>
          get(itemAtomsAtom).map((itemAtom, index) =>
            itemField(itemAtom as ItemAtom, index),
          ),
        );

        return {
          ...common,
          valueAtom: touchOnWrite(fieldAtom, runtime, fieldPath),
          itemsAtom,
          itemAtomsAtom,
          insert: atom(
            null,
            (
              _get,
              set,
              { value, before }: { value: unknown; before?: ItemAtom },
            ) => {
              set(itemAtomsAtom, { type: "insert", value, before });
            },
          ),
          remove: atom(null, (_get, set, itemAtom: ItemAtom) => {
            set(itemAtomsAtom, { type: "remove", atom: itemAtom });
          }),
        } as unknown as ArrayField;
      })

      .otherwise((otherFieldType) => {
        const fieldAtom = touchOnWrite(
          createAtomForField(baseAtom, key),
          runtime,
          fieldPath,
        );

        return match(otherFieldType)
          .returnType<Field>()
          .with(
            "text",
            () =>
              ({
                ...common,
                atom: fieldAtom,
              }) as unknown as TextField,
          )
          .with(
            "number",
            () =>
              ({
                ...common,
                atom: fieldAtom,
              }) as unknown as NumberField,
          )
          .with(
            "boolean",
            () =>
              ({
                ...common,
                atom: fieldAtom,
              }) as unknown as BooleanField,
          )
          .with(
            "date",
            () =>
              ({
                ...common,
                atom: fieldAtom,
              }) as unknown as DateField,
          )
          .with(
            "select",
            () =>
              ({
                ...common,
                atom: fieldAtom,
                options: getEnumOptions(fieldSchema) ?? [],
              }) as unknown as SelectField<string>,
          )
          .exhaustive();
      });
  }

  return { fields: fields as InferFieldsFromSchema<TSchema> };
}

export type FormAtomOptions = {
  normalizeNull?: boolean;
};

export function formAtom<
  TSchema extends ZodObjectSchema,
  TData = z.infer<TSchema>,
>(schema: TSchema, data: TData, options: FormAtomOptions = {}) {
  const { normalizeNull = true } = options;

  const initial = coerceInitial(schema, data, normalizeNull);

  const baseAtom = atom<TData>(initial);
  const originalAtom = atom<TData>(initial);

  const runtime: FormRuntime = {
    issues: atom((get) => issuesByPath(schema, get(baseAtom))),
    external: atom<FormErrors>({}),
    touched: atom<Record<string, true>>({}),
    submitted: atom(false),
    mounted: atom<Record<string, number>>({}),
  };

  const submittingAtom = atom(false);

  const { fields } = atomTree(schema, baseAtom, runtime);

  const errorsAtom = atom<FormErrors>((get) => {
    const issues = get(runtime.issues);
    const external = get(runtime.external);

    return { ...issues, ...external };
  });

  const state = {
    values: atom((get) => get(baseAtom)),
    errors: errorsAtom,
    isValid: atom((get) => 0 === Object.keys(get(runtime.issues)).length),
    isDirty: atom(
      (get) =>
        JSON.stringify(get(baseAtom)) !== JSON.stringify(get(originalAtom)),
    ),
    submitting: atom((get) => get(submittingAtom)),
    submitted: atom((get) => get(runtime.submitted)),
    touched: atom((get) => get(runtime.touched)),
    mounted: atom((get) => Object.keys(get(runtime.mounted))),
  };

  const actions = {
    reset: atom(null, (get, set) => {
      set(baseAtom, get(originalAtom));
      set(runtime.external, {});
      set(runtime.touched, {});
      set(runtime.submitted, false);
    }),

    setInitial: atom(null, (_get, set, next: TData) => {
      const coerced = coerceInitial(schema, next, normalizeNull);

      set(baseAtom, coerced);
      set(originalAtom, coerced);
      set(runtime.external, {});
      set(runtime.touched, {});
      set(runtime.submitted, false);
    }),

    setValues: atom(null, (_get, set, next: TData) => {
      set(baseAtom, next);
    }),

    setErrors: atom(
      null,
      (
        _get,
        set,
        violations: FormErrors | { path: string; message: string }[],
      ) => {
        const errors: FormErrors = {};

        if (Array.isArray(violations)) {
          for (const { path, message } of violations) {
            const key = normalizeErrorPath(path);

            errors[key] = [...(errors[key] ?? []), message];
          }
        } else {
          for (const [path, messages] of Object.entries(violations)) {
            errors[normalizeErrorPath(path)] = messages;
          }
        }

        set(runtime.external, errors);
        set(runtime.submitted, true);
      },
    ),

    clearErrors: atom(null, (_get, set) => {
      set(runtime.external, {});
    }),

    touch: atom(null, (_get, set, path: string) => {
      set(runtime.touched, (previous) => ({ ...previous, [path]: true }));
    }),

    submit: atom(
      null,
      async (
        get,
        set,
        submitter?: (data: TData) => Promise<unknown>,
      ): Promise<SubmitResult<TData>> => {
        set(runtime.submitted, true);
        set(runtime.external, {});

        const data = get(baseAtom);
        const errors = issuesByPath(schema, data);

        if (0 < Object.keys(errors).length) {
          const mounted = get(runtime.mounted);

          return {
            success: false,
            data: null,
            errors,
            unbound: Object.keys(errors).filter((path) => !(path in mounted)),
          };
        }

        if (!submitter) {
          return { success: true, data, errors: null, unbound: null };
        }

        set(submittingAtom, true);

        try {
          await submitter(data);

          return { success: true, data, errors: null, unbound: null };
        } finally {
          set(submittingAtom, false);
        }
      },
    ),
  };

  return { fields, actions, state };
}
